import { useEffect, useState } from 'react';
import { 
    Box, CircularProgress, TextField, Typography, Paper, 
    Container, Button, Avatar, Fade, Grid, Stack,
    Tooltip, IconButton
} from '@mui/material';
import {
    Campaign as ComplaintIcon,
    CalendarMonth as DateIcon,
    Description as NoteIcon,
    Send as SendIcon,
    Info as InfoIcon,
    History as HistoryIcon,
    ArrowBack as ArrowBackIcon,
    HelpOutline as HelpIcon
} from '@mui/icons-material';
import { createComplain } from '../../redux/complainRelated/complainHandle';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate } from '../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const float = keyframes`0% { transform: translateY(0px); } 50% { transform: translateY(-5px); } 100% { transform: translateY(0px); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 40px 0 60px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -80px;
    position: relative;
    z-index: 2;
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 32px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.1) !important;
    overflow: hidden;
    padding: 40px;
`;

const StyledTextField = styled(TextField)`
    & .MuiOutlinedInput-root {
        background-color: #f8fafc;
        border-radius: 16px;
        transition: all 0.3s ease;
        
        &:hover {
            background-color: #f1f5f9;
        }
        
        &.Mui-focused {
            background-color: white;
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
        }
        
        & fieldset {
            border-color: #e2e8f0;
            border-width: 1px;
        }
    }
`;

const FormIcon = styled(Avatar)`
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
    width: 60px !important;
    height: 60px !important;
    box-shadow: 0 10px 20px rgba(99, 102, 241, 0.3) !important;
    animation: ${float} 3s ease-in-out infinite;
`;

const GuidelineBox = styled(Box)`
    background: #eff6ff;
    border-radius: 20px;
    padding: 24px;
    border: 1px solid #dbeafe;
`;

const GuidelineItem = styled(Box)`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const StudentComplain = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showToast } = useToast();
    
    const [date, setDate] = useState(getCurrentBSDate());
    const [complaint, setComplaint] = useState("");

    const { currentUser } = useSelector(state => state.user);
    const { status: complainStatus, loading, response: complainResponse, error: complainError } = useSelector(state => state.complain);

    const user = currentUser?._id;
    const school = currentUser?.school?._id;

    const fields = {
        user,
        userModel: "student",
        date,
        complaint,
        school,
    };

    const submitHandler = (event) => {
        event.preventDefault();
        if (!date || !complaint) {
            showToast("Please fill all required fields", "warning");
            return;
        }
        dispatch(createComplain(fields));
    };

    useEffect(() => {
        if (complainStatus === "createSuccess") {
            setComplaint("");
            setDate(getCurrentBSDate());
            showToast("Your complaint has been submitted successfully!", "success");
        }
        else if (complainResponse) {
            showToast(complainResponse, "info");
        }
        else if (complainError) {
            showToast("Failed to submit complaint. Please try again.", "error");
        }
    }, [complainStatus, complainResponse, complainError, showToast]);

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl" >
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FormIcon>
                                <ComplaintIcon sx={{ fontSize: 30 }} />
                            </FormIcon>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em', mb: 0.5 }}>
                                    Submit a Concern
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400 }}>
                                    Share your feedback or report an issue with the administration.
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="md" style={{marginTop:"2rem"}}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <GlassCard>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                                <NoteIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                    Complaint Details
                                </Typography>
                            </Box>

                            <form onSubmit={submitHandler}>
                                <Stack spacing={4}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b', mb: 1.5, ml: 1 }}>
                                            Date of Incident
                                        </Typography>
                                        <CustomNepaliDatePicker
                                            value={date}
                                            onChange={setDate}
                                            fullWidth
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b', mb: 1.5, ml: 1 }}>
                                            Describe your concern
                                        </Typography>
                                        <StyledTextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            placeholder="What happened? Please be as detailed as possible..."
                                            value={complaint}
                                            onChange={(e) => setComplaint(e.target.value)}
                                            required
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            type="submit"
                                            disabled={loading}
                                            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                            sx={{
                                                borderRadius: '16px',
                                                px: 6,
                                                py: 1.8,
                                                bgcolor: '#6366f1',
                                                fontWeight: 800,
                                                fontSize: '1rem',
                                                textTransform: 'none',
                                                boxShadow: '0 10px 20px -5px rgba(99, 102, 241, 0.4)',
                                                '&:hover': {
                                                    bgcolor: '#4f46e5',
                                                    boxShadow: '0 15px 25px -5px rgba(99, 102, 241, 0.5)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            {loading ? 'Submitting...' : 'Submit Complaint'}
                                        </Button>
                                    </Box>
                                </Stack>
                            </form>
                        </GlassCard>
                    </Grid>

                    <Grid item xs={12}>
                        <Fade in timeout={1200}>
                            <GuidelineBox>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <HelpIcon sx={{ color: '#3b82f6' }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e40af' }}>
                                        Filing Guidelines
                                    </Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <GuidelineItem>
                                            <InfoIcon sx={{ color: '#60a5fa', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                                                Be specific about the date, time, and location of the incident.
                                            </Typography>
                                        </GuidelineItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <GuidelineItem>
                                            <InfoIcon sx={{ color: '#60a5fa', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                                                Keep your description clear and factual to help us investigate.
                                            </Typography>
                                        </GuidelineItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <GuidelineItem>
                                            <InfoIcon sx={{ color: '#60a5fa', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                                                Attach any supporting evidence if available (to be added soon).
                                            </Typography>
                                        </GuidelineItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <GuidelineItem>
                                            <InfoIcon sx={{ color: '#60a5fa', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                                                Your privacy is our priority; complaints are handled discreetly.
                                            </Typography>
                                        </GuidelineItem>
                                    </Grid>
                                </Grid>
                            </GuidelineBox>
                        </Fade>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default StudentComplain;
